import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormDropdown } from '@gokrew/ui/form';
import { TranslationNS } from '@/constants/translation-namespaces';
import type { AddEmployeeFormValues } from '@/types/employee';
import type { OnboardingTemplateOption } from '@/hooks/employees/useOnboardingTemplates';
import { defaultOnboardingWorkflow } from './onboardingWorkflowDefault';

interface OnboardingWorkflowFieldProps {
  /** The company's active onboarding templates; undefined while loading. */
  templates: OnboardingTemplateOption[] | undefined;
  /**
   * The workflow to preselect: a hire passes its Hired stage's template
   * (null when the stage has none). Left undefined (the Employees page), the
   * company's only template is preselected when it has exactly one.
   */
  defaultTaskListId?: string | null;
}

/**
 * "Onboarding workflow" on the Add employee form: None or one of the
 * company's onboarding templates, started in full once the employee is
 * saved. Hidden when the company has no template yet.
 */
export const OnboardingWorkflowField = ({
  templates,
  defaultTaskListId,
}: OnboardingWorkflowFieldProps) => {
  const { t } = useTranslation(TranslationNS.Employee);
  const { values, setFieldValue } = useFormikContext<AddEmployeeFormValues>();
  const seeded = useRef(false);

  const options = useMemo(
    () =>
      (templates ?? []).map((template) => ({
        value: template.id,
        label: template.name,
      })),
    [templates]
  );

  // Preselect once, when the templates arrive, and only if nobody picked yet.
  useEffect(() => {
    if (seeded.current || !templates) return;
    seeded.current = true;
    const preferred = defaultOnboardingWorkflow(templates, defaultTaskListId);
    if (preferred && !values.onboardingTaskListId) {
      void setFieldValue('onboardingTaskListId', preferred, false);
    }
  }, [
    templates,
    defaultTaskListId,
    values.onboardingTaskListId,
    setFieldValue,
  ]);

  if (!templates?.length) return null;

  return (
    <FormDropdown
      name="onboardingTaskListId"
      label={t('form.fields.onboarding_workflow.label')}
      placeholder={t('form.fields.onboarding_workflow.none')}
      infoText={t('form.fields.onboarding_workflow.info')}
      options={options}
      none
      displaySearchBar={options.length > 7}
    />
  );
};
